/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { InvoiceLine } from "./types";

type Props = {
  lines: InvoiceLine[];
  readOnly: boolean;
  onChange: (lines: InvoiceLine[]) => void;
};

export const emptyInvoiceLine = (): InvoiceLine => ({
  description: "",
  supplier_code: null,
  quantity: null,
  unit: null,
  unit_price: null,
  vat_rate: null,
  line_total: null,
});

/** A line's total: as entered, else quantity × unit price. */
export const lineTotal = (l: InvoiceLine) =>
  l.line_total ?? (l.quantity != null && l.unit_price != null ? Math.round(l.quantity * l.unit_price * 100) / 100 : null);

const num = (v: string) => (v.trim() === "" ? null : Number(v));

// What the supplier billed, line by line. Optional: an invoice can be just a
// total. Stock comes from deliveries, not from here.
export const InvoiceLinesTable = defineComponent<Props>({
  name: "InvoiceLinesTable",
  props: ["lines", "readOnly", "onChange"],
  setup(props) {
    const { t } = useLocalization();

    const update = (index: number, patch: Partial<InvoiceLine>) =>
      props.onChange(props.lines.map((l, i) => (i === index ? { ...l, ...patch } : l)));

    const cell = (index: number, key: keyof InvoiceLine, type: "text" | "number", cls: string) => {
      const line = props.lines[index];
      const value = line[key];
      return (
        <input
          type={type}
          step={type === "number" ? "any" : undefined}
          class={`input input-bordered input-xs w-full ${cls}`}
          disabled={props.readOnly}
          value={value ?? ""}
          onChange={(e: Event) => {
            const raw = (e.target as HTMLInputElement).value;
            update(index, { [key]: type === "number" ? num(raw) : raw || null } as Partial<InvoiceLine>);
          }}
        />
      );
    };

    return () => (
      <div class="overflow-x-auto">
        <table class="table table-sm">
          <thead>
            <tr>
              <th class="min-w-48">{t("inventory.invoices.col_description")}</th>
              <th class="w-20">{t("inventory.recipe.col_qty")}</th>
              <th class="w-20">{t("inventory.recipe.col_unit")}</th>
              <th class="w-24">{t("inventory.purchases.col_unit_price")}</th>
              <th class="w-20">{t("inventory.invoices.col_vat_rate")}</th>
              <th class="w-28 text-right">{t("inventory.purchases.col_total")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {props.lines.map((line, index) => (
              <tr key={index}>
                <td>
                  {cell(index, "description", "text", "")}
                  {line.supplier_code && (
                    <div class="mt-0.5 text-[11px] text-base-content/40">#{line.supplier_code}</div>
                  )}
                </td>
                <td>{cell(index, "quantity", "number", "text-right")}</td>
                <td>{cell(index, "unit", "text", "")}</td>
                <td>{cell(index, "unit_price", "number", "text-right")}</td>
                <td>{cell(index, "vat_rate", "number", "text-right")}</td>
                <td class="text-right">
                  {props.readOnly ? (
                    <span class="font-medium">{lineTotal(line)?.toFixed(2) ?? "—"}</span>
                  ) : (
                    <input
                      type="number"
                      step="0.01"
                      class="input input-bordered input-xs w-full text-right"
                      placeholder={lineTotal({ ...line, line_total: null })?.toFixed(2) ?? ""}
                      value={line.line_total ?? ""}
                      onChange={(e: Event) => update(index, { line_total: num((e.target as HTMLInputElement).value) })}
                    />
                  )}
                </td>
                <td>
                  {!props.readOnly && (
                    <button
                      class="btn btn-ghost btn-xs"
                      onClick={() => props.onChange(props.lines.filter((_, i) => i !== index))}
                    >
                      <span class="material-symbols-outlined text-base">close</span>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  },
});
